export enum AvailableLocales {
  FR_FR = 'fr-FR',
  EN_US = 'en-US',
  EN_GB = 'en-GB',
  ES_ES = 'es-ES',
  ES_MX = 'es-MX',
  IT_IT = 'it-IT',
  PL_PL = 'pl-PL',
  PT_BR = 'pt-BR',
  HT_HT = 'ht-HT',
  FR_CA = 'fr-CA',
  DE_DE = 'de-DE',
  JA_JP = 'ja-JP',
  HI_IN = 'hi-IN',
  ZH_CN = 'zh-CN',
  ZH_TW = 'zh-TW',
  KO_KR = 'ko-KR',
  // Expanded language support (worker-facing)
  AR = 'ar',
  ZH_HK = 'zh-HK',
  CS_CZ = 'cs-CZ',
  DA_DK = 'da-DK',
  NL_NL = 'nl-NL',
  FP_PH = 'fp-PH',
  EL_GR = 'el-GR',
  HE = 'he',
  HU_HU = 'hu-HU',
  NB_NO = 'nb-NO',
  PT_PT = 'pt-PT',
  RO_RO = 'ro-RO',
  RU_RU = 'ru-RU',
  SR_RS = 'sr-RS',
  SV_SE = 'sv-SE',
  TR_TR = 'tr-TR',
  UK_UA = 'uk-UA',
  VI_VN = 'vi-VN',
  SQ_AL = 'sq-AL',
  AM_ET = 'am-ET',
  BN = 'bn',
  BS_BA = 'bs-BA',
  BG_BG = 'bg-BG',
  CA = 'ca',
  HR_HR = 'hr-HR',
  FA_AF = 'fa-AF',
  ET_EE = 'et-EE',
  FA_IR = 'fa-IR',
  FI_FI = 'fi-FI',
  KA_GE = 'ka-GE',
  GU_IN = 'gu-IN',
  KK_KZ = 'kk-KZ',
  LV_LV = 'lv-LV',
  LT_LT = 'lt-LT',
  ML_IN = 'ml-IN',
  PA_IN = 'pa-IN',
  SK_SK = 'sk-SK',
  SL_SI = 'sl-SI',
  SO_SO = 'so-SO',
  TA_IN = 'ta-IN',
  TE_IN = 'te-IN',
  TH_TH = 'th-TH',
  UR = 'ur',
  AF_ZA = 'af-ZA',
  HY_AM = 'hy-AM',
  AZ_AZ = 'az-AZ',
  HA = 'ha',
  ID_ID = 'id-ID',
  KN_IN = 'kn-IN',
  MK_MK = 'mk-MK',
  MS = 'ms',
  MR_IN = 'mr-IN',
  MN_MN = 'mn-MN',
  PS = 'ps',
  SI_LK = 'si-LK',
  SW = 'sw',
  UZ_UZ = 'uz-UZ',
}

/**
 * SupportedLocale — type-safe union of locale strings accepted by the UI.
 *
 * This union includes both the canonical "dash" forms used in the codebase
 * (e.g. `en-US`) and the underscore variants that are stored in our database
 * (e.g. `en_US`). Accepting both formats lets callers pass values directly from
 * the DB without manual conversion.
 *
 * Notes:
 *  - The canonical runtime enum for locales is `AvailableLocales` (dash forms).
 *  - Use `normalizeLocale()` in `frontends/wx-ui/src/utils/translations/index.ts`
 *    to convert a `SupportedLocale` into an `AvailableLocales` value for
 *    lookups and runtime behavior.
 *
 * Examples:
 *  - 'en-US'  // dash format, matches AvailableLocales.EN_US
 *  - 'en_US'  // underscore format as stored in DB — normalized to 'en-US'
 *
 * Why both forms?
 *  - Back-end storage historically uses underscores. Accepting both makes the
 *    prop ergonomic for callers while preserving strong typing in TypeScript.
 */
export type SupportedLocale =
  | 'fr-FR'
  | 'fr_FR'
  | 'en-US'
  | 'en_US'
  | 'en-GB'
  | 'en_GB'
  | 'es-ES'
  | 'es_ES'
  | 'es-MX'
  | 'es_MX'
  | 'it-IT'
  | 'it_IT'
  | 'pl-PL'
  | 'pl_PL'
  | 'pt-BR'
  | 'pt_BR'
  | 'ht-HT'
  | 'ht_HT'
  | 'fr-CA'
  | 'fr_CA'
  | 'de-DE'
  | 'de_DE'
  | 'ja-JP'
  | 'ja_JP'
  | 'hi-IN'
  | 'hi_IN'
  | 'zh-CN'
  | 'zh_CN'
  | 'zh-TW'
  | 'zh_TW'
  | 'ko-KR'
  | 'ko_KR'
  // Expanded language support (worker-facing)
  | 'ar'
  | 'zh-HK'
  | 'zh_HK'
  | 'cs-CZ'
  | 'cs_CZ'
  | 'da-DK'
  | 'da_DK'
  | 'nl-NL'
  | 'nl_NL'
  | 'fp-PH'
  | 'fp_PH'
  | 'el-GR'
  | 'el_GR'
  | 'he'
  | 'hu-HU'
  | 'hu_HU'
  | 'nb-NO'
  | 'nb_NO'
  | 'pt-PT'
  | 'pt_PT'
  | 'ro-RO'
  | 'ro_RO'
  | 'ru-RU'
  | 'ru_RU'
  | 'sr-RS'
  | 'sr_RS'
  | 'sv-SE'
  | 'sv_SE'
  | 'tr-TR'
  | 'tr_TR'
  | 'uk-UA'
  | 'uk_UA'
  | 'vi-VN'
  | 'vi_VN'
  | 'sq-AL'
  | 'sq_AL'
  | 'am-ET'
  | 'am_ET'
  | 'bn'
  | 'bs-BA'
  | 'bs_BA'
  | 'bg-BG'
  | 'bg_BG'
  | 'ca'
  | 'hr-HR'
  | 'hr_HR'
  | 'fa-AF'
  | 'fa_AF'
  | 'et-EE'
  | 'et_EE'
  | 'fa-IR'
  | 'fa_IR'
  | 'fi-FI'
  | 'fi_FI'
  | 'ka-GE'
  | 'ka_GE'
  | 'gu-IN'
  | 'gu_IN'
  | 'kk-KZ'
  | 'kk_KZ'
  | 'lv-LV'
  | 'lv_LV'
  | 'lt-LT'
  | 'lt_LT'
  | 'ml-IN'
  | 'ml_IN'
  | 'pa-IN'
  | 'pa_IN'
  | 'sk-SK'
  | 'sk_SK'
  | 'sl-SI'
  | 'sl_SI'
  | 'so-SO'
  | 'so_SO'
  | 'ta-IN'
  | 'ta_IN'
  | 'te-IN'
  | 'te_IN'
  | 'th-TH'
  | 'th_TH'
  | 'ur'
  | 'af-ZA'
  | 'af_ZA'
  | 'hy-AM'
  | 'hy_AM'
  | 'az-AZ'
  | 'az_AZ'
  | 'ha'
  | 'id-ID'
  | 'id_ID'
  | 'kn-IN'
  | 'kn_IN'
  | 'mk-MK'
  | 'mk_MK'
  | 'ms'
  | 'mr-IN'
  | 'mr_IN'
  | 'mn-MN'
  | 'mn_MN'
  | 'ps'
  | 'si-LK'
  | 'si_LK'
  | 'sw'
  | 'uz-UZ'
  | 'uz_UZ';

export const LOCALE_EN_US = AvailableLocales.EN_US;
